import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";

/** Cache-bust so browsers discard stale/broken /video.mp4 copies after remux. */
const HERO_VIDEO_VERSION = "20260912c";
const HERO_VIDEO_SRC = `${process.env.PUBLIC_URL}/video.mp4?v=${HERO_VIDEO_VERSION}`;

function Header({ onReady }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, userName } = useAuth();
  const videoRef = useRef(null);
  const readySent = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const tryPlay = () => {
      if (!video || document.hidden) return;
      video.muted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay can be blocked until a user gesture; keep attributes ready.
        });
      }
    };

    const signalReady = () => {
      if (readySent.current) return;
      readySent.current = true;
      if (typeof onReady === "function") onReady();
      // Re-kick after the next paint once UI is visible.
      window.requestAnimationFrame(() => {
        window.setTimeout(tryPlay, 0);
        window.setTimeout(tryPlay, 100);
        window.setTimeout(tryPlay, 600);
      });
    };

    // React's muted prop is unreliable for autoplay policies — set both property and attribute.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.loop = true;

    const onCanPlay = () => {
      tryPlay();
      signalReady();
    };

    const onEnded = () => {
      // Backup if the loop attribute misses an end event.
      video.currentTime = 0;
      tryPlay();
    };

    // After the page-loader is gone, Chromium may have already paused the
    // video for being occluded/opacity-hidden. Resume when that happens.
    const onPause = () => {
      if (document.hidden || video.ended || !readySent.current) return;
      tryPlay();
    };

    const onStalled = () => {
      tryPlay();
    };

    const onVisibility = () => {
      if (!document.hidden) tryPlay();
    };

    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("playing", signalReady);
    video.addEventListener("error", signalReady);
    video.addEventListener("ended", onEnded);
    video.addEventListener("pause", onPause);
    video.addEventListener("stalled", onStalled);
    video.addEventListener("waiting", onStalled);
    document.addEventListener("visibilitychange", onVisibility);
    tryPlay();

    if (video.readyState >= 2) {
      signalReady();
    }

    const fallback = window.setTimeout(signalReady, 8000);
    const keepAlive = window.setInterval(() => {
      if (!readySent.current || document.hidden || video.ended) return;
      if (video.paused) tryPlay();
    }, 1500);

    return () => {
      window.clearTimeout(fallback);
      window.clearInterval(keepAlive);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("playing", signalReady);
      video.removeEventListener("error", signalReady);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("stalled", onStalled);
      video.removeEventListener("waiting", onStalled);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [onReady]);

  return (
    <header>
      <video
        key={HERO_VIDEO_VERSION}
        ref={videoRef}
        src={HERO_VIDEO_SRC}
        loop
        autoPlay
        muted
        playsInline
        preload="auto"
      />

      <div className="hero-content">
        {isLoggedIn && (
          <p className="header-welcome">
            {userName
              ? t("header.welcomeNamed", { name: userName })
              : t("header.welcome")}
          </p>
        )}

        <h1 className="hero-brand">
          <span className="hero-brand-name">Dblog</span>
          <span className="hero-brand-tag">Computer Solutions</span>
        </h1>

        <p className="hero-title">
          {isLoggedIn ? (
            <>
              {t("header.titleLoggedInBefore")}{" "}
              <Link
                to="contact"
                spy={true}
                smooth={true}
                duration={800}
                className="hero-contact-link"
              >
                {t("header.titleLoggedInCta")}
              </Link>{" "}
              {t("header.titleLoggedInAfter")}
            </>
          ) : (
            t("header.titleLoggedOut")
          )}
        </p>

        <p className="hero-support">{t("header.support")}</p>

        {!isLoggedIn && (
          <div className="hero-actions">
            <button type="button" onClick={() => navigate("/login")} className="btn">
              {t("header.logIn")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="btn btn-secondary"
            >
              {t("header.signUp")}
            </button>
          </div>
        )}
      </div>

      <div className="headerbg" id="headerbg"></div>
    </header>
  );
}

export default Header;
